import React, { useState } from 'react';
import { Box, Container, Typography, TextField, Button, Alert, Link } from '@mui/material';
import { SeoHelmet, CONTACT_SEO } from '../seo';

const CONTACT_EMAIL = 'contact@patricknoah.com';
const LISTEN_IMG = '/img/patrick-noah-listen-soundclound.jpg';
const PRESS_IMG_1 = '/img/facebook/481099923_1573961223545285_3802492480432519517_n.jpg';
const PRESS_IMG_2 = '/img/facebook/486766139_1601439824130758_2326674856448318314_n.jpg';
const PRESS_IMG_3 = '/img/facebook/486378977_1598975427710531_7308420942001854978_n.jpg';

function buildMailtoUrl({ nom, email, message }) {
  const subject = encodeURIComponent('Contact site Patrick Noah');
  const body = encodeURIComponent(`Nom : ${nom}\nEmail : ${email}\n\nMessage :\n${message}`);
  return `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
}

function openMailto(params) {
  window.location.href = buildMailtoUrl(params);
}

export function ContactForm() {
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [mailtoFallbackUrl, setMailtoFallbackUrl] = useState(null);
  const [fallbackServerMessage, setFallbackServerMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setStatus('sending');

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nom, email, message }),
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (res.ok) {
        setStatus('success');
        setMailtoFallbackUrl(null);
        setFallbackServerMessage(null);
        setNom('');
        setEmail('');
        setMessage('');
        return;
      }

      if (res.status === 503 || res.status === 404) {
        const url = buildMailtoUrl({ nom, email, message });
        setMailtoFallbackUrl(url);
        setFallbackServerMessage(typeof data.error === 'string' ? data.error : null);
        openMailto({ nom, email, message });
        setStatus('fallback');
        return;
      }

      setStatus('error');
      const base = data.error || "L'envoi a échoué.";
      const detail = data.detail ? ` — ${data.detail}` : '';
      setErrorMessage(base + detail);
    } catch {
      const url = buildMailtoUrl({ nom, email, message });
      setMailtoFallbackUrl(url);
      setFallbackServerMessage(null);
      openMailto({ nom, email, message });
      setStatus('fallback');
    }
  };

  return (
    <Container maxWidth="lg">
      <SeoHelmet {...CONTACT_SEO} />
      <Box component="article" sx={{ py: 4 }}>
        {/* Ligne 1 : formulaire + image (même hauteur = bas image = bas du form) */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 4,
            width: '100%',
            alignItems: { xs: 'stretch', sm: 'stretch' },
          }}
        >
          <Box
            sx={{
              flex: '1 1 50%',
              minWidth: 0,
              minHeight: 0,
              width: { xs: '100%', sm: '50%' },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="h2" color="primary" gutterBottom>
              Contact
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Contactez l'équipe pour toute demande.
              <br />
              Nous vous répondrons rapidement.
            </Typography>

            {status === 'success' && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Message envoyé. Merci, nous vous répondrons bientôt.
              </Alert>
            )}
            {status === 'fallback' && mailtoFallbackUrl && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {fallbackServerMessage && (
                  <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600 }}>
                    {fallbackServerMessage}
                  </Typography>
                )}
                <Typography variant="body2" component="div">
                  {fallbackServerMessage
                    ? 'En secours, vous pouvez tenter le lien ci-dessous ou copier l’adresse. '
                    : 'L’envoi depuis le site n’a pas abouti (réseau ou serveur). '}
                  Sans application mail par défaut sur cet appareil, un lien mail peut ne rien
                  ouvrir — utilisez alors la copie d’adresse.{' '}
                  <Link href={mailtoFallbackUrl}>Ouvrir / préparer l’e-mail</Link>
                  {' — '}
                  <Box component="span" sx={{ userSelect: 'all', fontWeight: 600 }}>
                    {CONTACT_EMAIL}
                  </Box>
                </Typography>
              </Alert>
            )}
            {status === 'error' && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
              <TextField
                label="Nom"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                fullWidth
                required
                variant="outlined"
              />
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
                variant="outlined"
              />
              <TextField
                label="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                fullWidth
                required
                multiline
                rows={4}
                variant="outlined"
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={status === 'sending'}
                sx={{ alignSelf: 'flex-start' }}
              >
                {status === 'sending' ? 'Envoi…' : 'Envoyer'}
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              flex: '1 1 50%',
              minWidth: 0,
              minHeight: 0,
              width: { xs: '100%', sm: '50%' },
              display: 'flex',
              alignItems: 'stretch',
            }}
          >
            <Box
              sx={{
                borderRadius: 6,
                overflow: 'hidden',
                width: '100%',
                height: { xs: 320, sm: '100%' },
                background: 'transparent',
              }}
            >
              <Box
                component="img"
                src={LISTEN_IMG}
                alt="Patrick Noah en studio"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center top',
                  display: 'block',
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Presse : sous la ligne formulaire + image, pleine largeur */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="overline" sx={{ display: 'block', mb: 1, color: 'primary.main', fontWeight: 700, letterSpacing: '0.12em' }}>
            Presse / Radio / TV
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 2,
            }}
          >
            {[PRESS_IMG_1, PRESS_IMG_2, PRESS_IMG_3].map((src) => (
              <Box
                key={src}
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  height: { xs: 150, sm: 160, md: 170 },
                  boxShadow: '0 12px 40px rgba(26, 43, 72, 0.10)',
                }}
              >
                <Box
                  component="img"
                  src={src}
                  alt="Patrick Noah - presse"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    display: 'block',
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Container>
  );
}

export default ContactForm;
